import { differenceInCalendarDays } from 'date-fns';
import { useEffect, useRef, useState } from 'react';

import {
  getUserTraits,
  TRELLO_M3_OPT_IN_DATE,
} from '@trello/atlassian-personalization';
import { useAaId } from '@trello/authentication';
import type { TouchpointSourceType } from '@trello/cross-flow';
import { useFeatureGate } from '@trello/feature-gate-client';

const DELAY_IN_DAYS = 21;

export const useIsNotProductivityUserOrIsEligible = (
  source: TouchpointSourceType,
) => {
  const [isEligible, setIsEligible] = useState(false);
  const isLoadingTrait = useRef(false);

  const aaId = useAaId();

  const { value: isInPersonalProductivity, loading: loadingProductivityGate } =
    useFeatureGate('trello_personal_productivity_release');
  const { value: isAdEnabledForProductivity } = useFeatureGate(
    'ghost_show_xf_ads_in_m3',
  );

  useEffect(() => {
    const getTrait = async () => {
      isLoadingTrait.current = true;
      try {
        if (!aaId) {
          setIsEligible(false);
          return;
        }
        const traits = await getUserTraits(aaId);
        const optInTrait = traits?.[TRELLO_M3_OPT_IN_DATE];
        if (!optInTrait) {
          setIsEligible(false);
          return;
        }
        const traitOptInDate = new Date(optInTrait as string);
        const currentDate = new Date();
        if (
          differenceInCalendarDays(currentDate, traitOptInDate) >
            DELAY_IN_DAYS &&
          source === 'boardScreen'
        ) {
          setIsEligible(true);
        } else {
          setIsEligible(false);
        }
      } finally {
        isLoadingTrait.current = false;
      }
    };
    // if the user is not in personal productivity, this eligibility check passes
    if (!isInPersonalProductivity && !loadingProductivityGate) {
      setIsEligible(true);
      return;
    }
    if (!isAdEnabledForProductivity) {
      setIsEligible(false);
      return;
    }
    if (!isLoadingTrait.current) {
      getTrait();
    }
  }, [
    aaId,
    isInPersonalProductivity,
    isAdEnabledForProductivity,
    loadingProductivityGate,
    source,
  ]);

  return { isEligible };
};
